
import { Component, Vue } from "vue-property-decorator";
import { actions, LoginAttempt } from "@/survey";
import InputWithLabel from "@/components/InputWithLabel.vue";
import PasswordWithLabel from "@/components/PasswordWithLabel.vue";
import { apiLocation } from "@/store";

@Component({
  components: { InputWithLabel, PasswordWithLabel }
})
export default class LoginForm extends Vue {
  callbackStorageKey = "__PINPOINT_OAUTH_CALLBACK";
  completeStorageKey = "__PINPOINT_OAUTH_COMPLETE";
  pendingStateStorageKey = "__PINPOINT_OAUTH_PENDING_STATE";
  _oauthWindow: any = null;
  _oauthInterval: any = null;
  _oauthMessageHandler: any = null;
  _oauthStorageHandler: any = null;
  _oauthBroadcastChannel: any = null;
  _oauthCompleted = false;
  state: any = (Math.random() + 1).toString(36).substring(7);
  returnState: string | null = null;
  sessionState: string | null = null;
  code: string | null = null;

  get ssoEnabled(){
    return this.$store.state.oauth['oauth.enabled'] === 'true'
  }
  data() {
    return {
      username: "",
      password: "",
      showImage: false,
      apiLocation: apiLocation,
      authCode: "",
      trying: false
    };
  }

  oauthpopup(options: any) {
    options.windowName = options.windowName || 'ConnectWithOAuth'; // should not include space for IE
    options.windowOptions = options.windowOptions || 'location=0,status=0,width=800,height=700';
    options.callback = options.callback || function () {
      window.location.reload();
    };
    this._oauthCompleted = false;

    const finish = (oauthResult: any) => {
      if (this._oauthCompleted) {
        return;
      }
      this._oauthCompleted = true;
      window.clearInterval(this._oauthInterval);
      if (this._oauthMessageHandler) {
        window.removeEventListener('message', this._oauthMessageHandler);
        this._oauthMessageHandler = null;
      }
      if (this._oauthStorageHandler) {
        window.removeEventListener('storage', this._oauthStorageHandler);
        this._oauthStorageHandler = null;
      }
      if (this._oauthBroadcastChannel) {
        this._oauthBroadcastChannel.close();
        this._oauthBroadcastChannel = null;
      }
      try {
        localStorage.removeItem(this.pendingStateStorageKey);
        localStorage.removeItem(this.callbackStorageKey);
      } catch (e) {
        // Storage may be unavailable in strict private browsing modes.
      }
      options.callback(oauthResult);
    };

    const handleOauthResult = (oauthResult: any) => {
      if (!oauthResult || oauthResult.type !== 'pinpoint-oauth-callback') {
        return;
      }
      this.returnState = oauthResult.state;
      this.sessionState = oauthResult.sessionState;
      this.code = oauthResult.code;
      finish(oauthResult);
    };

    this._oauthMessageHandler = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }
      handleOauthResult(event.data || {});
    };

    this._oauthStorageHandler = (event: StorageEvent) => {
      if (event.key !== this.callbackStorageKey || !event.newValue) {
        return;
      }
      try {
        handleOauthResult(JSON.parse(event.newValue));
      } catch (e) {
        // Ignore malformed storage events.
      }
    };

    window.addEventListener('message', this._oauthMessageHandler);
    window.addEventListener('storage', this._oauthStorageHandler);
    try {
      this._oauthBroadcastChannel = new BroadcastChannel('pinpoint-oauth');
      this._oauthBroadcastChannel.onmessage = (event: MessageEvent) => handleOauthResult(event.data || {});
    } catch (e) {
      this._oauthBroadcastChannel = null;
    }

    try {
      localStorage.setItem(this.pendingStateStorageKey, this.state);
      localStorage.removeItem(this.callbackStorageKey);
    } catch (e) {
      // Storage may be unavailable in strict private browsing modes.
    }

    this._oauthWindow = window.open(options.path, options.windowName, options.windowOptions);
    this._oauthInterval = window.setInterval(function (this: any) {
      try {
        if (!this._oauthWindow || this._oauthWindow.closed) {
          window.clearInterval(this._oauthInterval);
          return;
        }
        const windowParams = new URLSearchParams(this._oauthWindow.location.search);
        if (windowParams.has('state') || windowParams.has('code') || windowParams.has('error')) {
          const oauthResult = {
            code: windowParams.get('code'),
            state: windowParams.get('state'),
            sessionState: windowParams.get('session_state'),
            error: windowParams.get('error'),
            errorDescription: windowParams.get('error_description')
          };
          this.returnState = oauthResult.state;
          this.sessionState = oauthResult.sessionState;
          this.code = oauthResult.code;
          finish(oauthResult);
        }
      } catch (e) {
        // Ignore cross-origin access while the popup is still on Microsoft.
      }
    }.bind(this), 1000);
  };

  doOauth() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const component = this;
    const oauthScope = component.$store.state.oauth['oauth.scope'];
    const hasOauthScope = oauthScope.length > 0;
    const oauthPath = `${component.$store.state.oauth['oauth.server']}?client_id=${component.$store.state.oauth['oauth.client_id']}&response_type=code&state=${this.state}&redirect_uri=${encodeURIComponent(component.$store.state.oauth['oauth.redirect_uri'])}${hasOauthScope ? `&scope=${encodeURIComponent(oauthScope)}` : ''}`
    this.oauthpopup({
      path: oauthPath,
      callback: function (oauthResult: any) {
        if (oauthResult.error) {
          alert(`SSO login failed: ${oauthResult.errorDescription || oauthResult.error}`);
          return;
        }
        if (component.state !== component.returnState) {
          const message = "Looks like a bad request - states don't match";
          component.notifyOauthWindow({ success: false, message });
          alert(message);
          throw(message);
        }
        if (!component.code) {
          const message = "Microsoft sign-in did not return an authorization code. Please try again.";
          component.notifyOauthWindow({ success: false, message });
          alert(message);
          return;
        }
        component.$store
          .dispatch(actions.TRY_OAUTH_LOGIN, {
            code: component.code,
            success: () => {
              component.notifyOauthWindow({ success: true });
              component.$router.push(component.getNext());
            },
            failure: (message: string) => {
              component.notifyOauthWindow({ success: false, message });
            }
          })
          .finally(() => (component.$data.trying = false));
      }
    });
  }

  notifyOauthWindow(result: { success: boolean; message?: string }) {
    const oauthResult = {
      type: "pinpoint-oauth-complete",
      success: result.success,
      message: result.message,
      sentAt: new Date().getTime()
    };

    if (this._oauthWindow && !this._oauthWindow.closed) {
      this._oauthWindow.postMessage(oauthResult, window.location.origin);
      if (result.success) {
        window.setTimeout(() => {
          if (this._oauthWindow && !this._oauthWindow.closed) {
            this._oauthWindow.close();
          }
        }, 500);
      }
    }

    try {
      const broadcastChannel = new BroadcastChannel("pinpoint-oauth");
      broadcastChannel.postMessage(oauthResult);
      broadcastChannel.close();
    } catch (e) {
      // BroadcastChannel may be unavailable in older browsers.
    }

    try {
      localStorage.setItem(this.completeStorageKey, JSON.stringify(oauthResult));
    } catch (e) {
      // Storage may be unavailable in strict private browsing modes.
    }
  }

  get loginState() {
    return this.$store.state.authentication.loginState;
  }

  get title() {
    return this.$store.state.oauth['web.title'];
  }

  returnFalse() {
    return false;
  }

  getNext() {
    const next = this.$route.query.redirect
        ? this.$route.query.redirect + ""
        : "/";
    return next;
  }

  tryLogin() {
    this.$data.trying = true;
    const loginAttempt: LoginAttempt = {
      credentials: {
        username: this.$data.username,
        password: this.$data.password
      },
      success: () => {
        this.$router.push(this.getNext());
      }
    };
    this.$store
        .dispatch(actions.TRY_LOGIN, loginAttempt)
        .finally(() => (this.$data.trying = false));
  }

  tryAuthCode() {
    this.$data.trying = true;
    this.$store.dispatch(actions.TRY_2FA, {
      code: this.$data.authCode,
      success: () => {
        this.$router
            .push(this.getNext())
            .finally(() => (this.$data.trying = false));
      }
    });
  }
}
